// src/types/grades.ts
var V_GRADES = [
  "VB",
  "V0",
  "V1",
  "V2",
  "V3",
  "V4",
  "V5",
  "V6",
  "V7",
  "V8",
  "V9",
  "V10",
  "V11",
  "V12",
  "V13",
  "V14",
  "V15",
  "V16",
  "V17"
];
var isVGrade = (value) => {
  return typeof value === "string" && V_GRADES.includes(value);
};
var getGradeIndex = (grade) => {
  return V_GRADES.indexOf(grade);
};
var compareGrades = (a, b) => {
  return getGradeIndex(a) - getGradeIndex(b);
};
var GRADE_MODIFIERS = ["+", "-", null];
var isGradeModifier = (value) => {
  return value === "+" || value === "-" || value === null;
};

// src/types/grading-preferences.ts
var GRADING_SYSTEMS = ["v-scale", "font", "color-only"];
var isGradingSystem = (value) => {
  return typeof value === "string" && GRADING_SYSTEMS.includes(value);
};
var GRADE_DISPLAY_PREFERENCES = ["v-scale", "font", "gym-default"];
var isGradeDisplayPreference = (value) => {
  return typeof value === "string" && GRADE_DISPLAY_PREFERENCES.includes(value);
};

// src/types/common.ts
var CLIMB_OUTCOMES = ["send", "attempt"];
var isClimbOutcome = (value) => {
  return typeof value === "string" && CLIMB_OUTCOMES.includes(value);
};
var ROUTE_STATUSES = ["draft", "published", "retired"];
var isRouteStatus = (value) => {
  return typeof value === "string" && (ROUTE_STATUSES.includes(value) || value === "active");
};
var normalizeRouteStatus = (status) => {
  if (status === "active") return "published";
  if (ROUTE_STATUSES.includes(status)) return status;
  return "published";
};

// src/types/roles.ts
var GYM_ROLES = [
  "owner",
  "manager",
  "shift_lead",
  "setter",
  "instructor",
  "staff",
  "member",
  "climber"
  // Legacy alias
];
var STAFF_ROLES = [
  "owner",
  "manager",
  "shift_lead",
  "setter",
  "instructor",
  "staff"
];
var isGymRole = (value) => {
  return typeof value === "string" && GYM_ROLES.includes(value);
};
var normalizeGymRole = (role) => {
  return role === "climber" ? "member" : role;
};
var hasRolePrivilege = (userRole, requiredRole) => {
  const normalizedUser = normalizeGymRole(userRole);
  const normalizedRequired = normalizeGymRole(requiredRole);
  const userIndex = GYM_ROLES.indexOf(normalizedUser);
  const requiredIndex = GYM_ROLES.indexOf(normalizedRequired);
  return userIndex <= requiredIndex;
};
var isStaffRole = (role) => {
  const normalized = normalizeGymRole(role);
  return STAFF_ROLES.includes(normalized);
};
var GYM_PERMISSIONS = [
  "manage_gym",
  // Edit gym settings, billing, integrations
  "manage_staff",
  // Add/remove staff, assign roles
  "manage_routes",
  // Create, edit, archive any route
  "edit_own_routes",
  // Edit routes the user created/set
  "moderate_comments",
  // Hide, delete comments; review flagged comments
  "moderate_media",
  // Hide, delete user-submitted media
  "ban_users",
  // Temporarily or permanently ban users from gym
  "review_flags",
  // Access flagged content queue
  "view_audit_log",
  // View moderation audit log
  "view_members"
  // View gym member list
];
var isGymPermission = (value) => {
  return typeof value === "string" && GYM_PERMISSIONS.includes(value);
};
var ROLE_DEFAULT_PERMISSIONS = {
  owner: [
    "manage_gym",
    "manage_staff",
    "manage_routes",
    "edit_own_routes",
    "moderate_comments",
    "moderate_media",
    "ban_users",
    "review_flags",
    "view_audit_log",
    "view_members"
  ],
  manager: [
    "manage_staff",
    "manage_routes",
    "edit_own_routes",
    "moderate_comments",
    "moderate_media",
    "ban_users",
    "review_flags",
    "view_audit_log",
    "view_members"
  ],
  shift_lead: ["moderate_comments", "moderate_media", "review_flags", "view_audit_log"],
  setter: ["edit_own_routes"],
  instructor: [],
  staff: [],
  member: []
};
var getDefaultPermissions = (role) => {
  const normalized = normalizeGymRole(role);
  return ROLE_DEFAULT_PERMISSIONS[normalized];
};
var roleHasDefaultPermission = (role, permission) => {
  return getDefaultPermissions(role).includes(permission);
};
var APP_ROLES = ["super_admin", "support"];
var isAppRole = (value) => {
  return typeof value === "string" && APP_ROLES.includes(value);
};
var hasPermission = (membership, permission) => {
  if (membership.isBanned) {
    return false;
  }
  if (membership.revokedPermissions?.includes(permission)) {
    return false;
  }
  if (membership.permissions?.includes(permission)) {
    return true;
  }
  return roleHasDefaultPermission(membership.role, permission);
};
var getEffectivePermissions = (membership) => {
  if (membership.isBanned) {
    return [];
  }
  const defaultPerms = [...getDefaultPermissions(membership.role)];
  const explicitGrants = membership.permissions ?? [];
  const revocations = membership.revokedPermissions ?? [];
  const allPerms = /* @__PURE__ */ new Set([...defaultPerms, ...explicitGrants]);
  for (const revoked of revocations) {
    allPerms.delete(revoked);
  }
  return Array.from(allPerms);
};
var INVITATION_STATUSES = ["pending", "accepted", "expired"];
var isInvitationStatus = (value) => {
  return typeof value === "string" && INVITATION_STATUSES.includes(value);
};

// src/types/gym.ts
var getWallSections = (gym) => {
  if (!gym.floorPlan?.regions) return [];
  return gym.floorPlan.regions.map((region) => region.wallSectionName).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
};

// src/types/route-style.ts
var HOLD_STYLES = [
  "compression",
  "crack",
  "edge",
  "features",
  "jug",
  "pinch",
  "pocket",
  "sidepull",
  "sloper",
  "undercut",
  "volume"
];
var CLIMBING_STYLES = [
  "arete",
  "barrel",
  "chimney",
  "dihedral",
  "overhang",
  "roof",
  "slab",
  "step",
  "topout",
  "traverse",
  "vert",
  "wild"
];
var SKILL_STYLES = [
  "dynamic",
  "fingery",
  "power",
  "reachy",
  "sustained",
  "technical"
];
var ROUTE_STYLES = [...HOLD_STYLES, ...CLIMBING_STYLES, ...SKILL_STYLES];
var HOLD_STYLE_INFO = [
  { id: "compression", label: "Compression", description: "Squeeze holds together" },
  { id: "crack", label: "Crack", description: "Crack climbing technique" },
  { id: "edge", label: "Crimp", description: "Small crimp holds" },
  { id: "features", label: "Features", description: "Natural wall features" },
  { id: "jug", label: "Jug", description: "Large positive holds" },
  { id: "pinch", label: "Pinch", description: "Pinch holds featured" },
  { id: "pocket", label: "Pocket", description: "Pocket holds" },
  { id: "sidepull", label: "Side Pull", description: "Side-pulling holds" },
  { id: "sloper", label: "Sloper", description: "Sloper holds" },
  { id: "undercut", label: "Undercling", description: "Undercling holds" },
  { id: "volume", label: "Volume", description: "Volume features on wall" }
];
var CLIMBING_STYLE_INFO = [
  { id: "arete", label: "Aret\xE9", description: "Outside corner climbing" },
  { id: "barrel", label: "Barrel", description: "Barrel-shaped wall feature" },
  { id: "chimney", label: "Chimney", description: "Chimney climbing technique" },
  { id: "dihedral", label: "Dihedral", description: "Inside corner or dihedral" },
  { id: "overhang", label: "Overhang", description: "Overhanging wall angle" },
  { id: "roof", label: "Roof", description: "Horizontal ceiling climbing" },
  { id: "slab", label: "Slab", description: "Low angle, balance/footwork" },
  { id: "step", label: "Step", description: "Step features on wall" },
  { id: "topout", label: "Topout", description: "Climb over the top" },
  { id: "traverse", label: "Traverse", description: "Lateral movement" },
  { id: "vert", label: "Vert", description: "Vertical wall angle" },
  { id: "wild", label: "Wild", description: "Unusual or unconventional" }
];
var SKILL_STYLE_INFO = [
  { id: "dynamic", label: "Dynamic", description: "Dynamic moves required" },
  { id: "fingery", label: "Fingery", description: "Finger strength intensive" },
  { id: "power", label: "Power", description: "Strength moves, big pulls" },
  { id: "reachy", label: "Reachy", description: "Long reaches between holds" },
  { id: "sustained", label: "Sustained", description: "Endurance-focused" },
  { id: "technical", label: "Technical", description: "Precise footwork/body positioning" }
];
var ROUTE_STYLE_INFO = [
  ...HOLD_STYLE_INFO,
  ...CLIMBING_STYLE_INFO,
  ...SKILL_STYLE_INFO
];
var ROUTE_STYLE_CATEGORIES = [
  { key: "holdStyles", label: "Hold Styles", styles: HOLD_STYLE_INFO },
  { key: "styles", label: "Styles", styles: CLIMBING_STYLE_INFO },
  { key: "skills", label: "Skills", styles: SKILL_STYLE_INFO }
];
var LEGACY_STYLE_MAP = {
  // Hold style renames
  slopey: "sloper",
  crimpy: "edge",
  juggy: "jug",
  // Climbing style renames
  slabby: "slab",
  overhung: "overhang",
  // Skill style renames
  dyno: "dynamic",
  pumpy: "sustained",
  powerful: "power"
};
var LEGACY_STYLES = Object.keys(LEGACY_STYLE_MAP);
var isRouteStyle = (value) => {
  if (typeof value !== "string") return false;
  return ROUTE_STYLES.includes(value) || LEGACY_STYLES.includes(value);
};
var normalizeRouteStyle = (style) => {
  if (ROUTE_STYLES.includes(style)) {
    return style;
  }
  const mapped = LEGACY_STYLE_MAP[style];
  if (mapped !== void 0) {
    return mapped;
  }
  return null;
};
var normalizeRouteStyles = (styles) => {
  return styles.map(normalizeRouteStyle).filter((style) => style !== null);
};

// src/types/grade-submission.ts
var getGradeSubmissionId = (userId, routeId) => {
  return `${userId}_${routeId}`;
};

// src/types/quality-rating.ts
var QUALITY_RATING_VALUES = [1, 2, 3, 4, 5];
var isQualityRatingValue = (value) => {
  return typeof value === "number" && QUALITY_RATING_VALUES.includes(value);
};
var getQualityRatingId = (userId, routeId) => {
  return `${userId}_${routeId}`;
};

// src/types/gym-membership.ts
var getGymMembershipId = (userId, gymId) => {
  return `${userId}_${gymId}`;
};
var MAX_GYM_MEMBERSHIPS = 10;
var BAN_DURATIONS = {
  ONE_DAY: 1,
  ONE_WEEK: 7,
  ONE_MONTH: 30,
  THREE_MONTHS: 90,
  PERMANENT: null
};

// src/types/invitation.ts
var INVITATION_EXPIRY_DAYS = 7;
var isInvitationExpired = (invitation) => {
  const expiresAt = "toDate" in invitation.expiresAt ? invitation.expiresAt.toDate() : invitation.expiresAt;
  return /* @__PURE__ */ new Date() > expiresAt;
};

// src/types/owner-invitation.ts
var OWNER_INVITATION_EXPIRY_DAYS = 7;
var isOwnerInvitationExpired = (invitation) => {
  const expiresAt = "toDate" in invitation.expiresAt ? invitation.expiresAt.toDate() : invitation.expiresAt;
  return /* @__PURE__ */ new Date() > expiresAt;
};

// src/types/gym-request.ts
var GYM_REQUEST_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "changes_requested"
];
var isGymRequestStatus = (value) => {
  return typeof value === "string" && GYM_REQUEST_STATUSES.includes(value);
};

// src/types/feedback.ts
var FEEDBACK_TYPES = ["bug", "feature", "general"];
var isFeedbackType = (value) => {
  return typeof value === "string" && FEEDBACK_TYPES.includes(value);
};
var FEEDBACK_PRIORITIES = ["low", "medium", "high"];
var isFeedbackPriority = (value) => {
  return typeof value === "string" && FEEDBACK_PRIORITIES.includes(value);
};
var FEEDBACK_STATUSES = ["new", "reviewed", "resolved"];
var isFeedbackStatus = (value) => {
  return typeof value === "string" && FEEDBACK_STATUSES.includes(value);
};

// src/types/flyer.ts
var FLYER_SIZES = ["letter", "half-letter"];
var isFlyerSize = (value) => {
  return typeof value === "string" && FLYER_SIZES.includes(value);
};
var FLYER_FORMATS = ["png", "svg"];
var isFlyerFormat = (value) => {
  return typeof value === "string" && FLYER_FORMATS.includes(value);
};

// src/types/comment.ts
var MAX_COMMENT_LENGTH = 500;
var MAX_REPLY_DEPTH = 1;
var COMMENT_MODERATION_STATUSES = ["visible", "hidden", "removed"];
var isCommentModerationStatus = (value) => {
  return typeof value === "string" && COMMENT_MODERATION_STATUSES.includes(value);
};
var isReply = (comment) => {
  return comment.parentId !== null;
};
var isValidCommentText = (text) => {
  return typeof text === "string" && text.trim().length > 0 && text.length <= MAX_COMMENT_LENGTH;
};

// src/types/content-flag.ts
var FLAG_CONTENT_TYPES = ["comment", "media", "route", "profile"];
var isFlagContentType = (value) => {
  return typeof value === "string" && FLAG_CONTENT_TYPES.includes(value);
};
var FLAG_REASONS = [
  "spam",
  "harassment",
  "hate_speech",
  "threats",
  "explicit",
  "misinformation",
  "other"
];
var isFlagReason = (value) => {
  return typeof value === "string" && FLAG_REASONS.includes(value);
};
var FLAG_REASON_LABELS = {
  spam: "Spam or advertising",
  harassment: "Harassment or bullying",
  hate_speech: "Hate speech or discrimination",
  threats: "Threats or violence",
  explicit: "Sexual or explicit content",
  misinformation: "False or misleading information",
  other: "Other"
};
var FLAG_STATUSES = ["pending", "reviewed", "actioned", "dismissed"];
var isFlagStatus = (value) => {
  return typeof value === "string" && FLAG_STATUSES.includes(value);
};
var FLAG_ACTIONS = ["removed", "hidden", "warned", "banned", "none"];
var MAX_FLAG_DETAILS_LENGTH = 500;
var isValidFlagDetails = (details) => {
  return typeof details === "string" && details.length <= MAX_FLAG_DETAILS_LENGTH;
};

// src/types/moderation-audit.ts
var MODERATION_ACTIONS = [
  // Content moderation
  "comment_hidden",
  "comment_deleted",
  "comment_restored",
  "media_hidden",
  "media_deleted",
  "media_restored",
  // User moderation
  "user_warned",
  "user_banned",
  "user_unbanned",
  // Flag handling
  "flag_reviewed",
  "flag_dismissed",
  // Staff management
  "staff_added",
  "staff_removed",
  "staff_role_changed",
  "permission_granted",
  "permission_revoked"
];
var isModerationAction = (value) => {
  return typeof value === "string" && MODERATION_ACTIONS.includes(value);
};
var MODERATION_TARGET_TYPES = ["comment", "media", "user", "flag", "staff"];
var MODERATION_ACTION_LABELS = {
  comment_hidden: "Comment hidden",
  comment_deleted: "Comment deleted",
  comment_restored: "Comment restored",
  media_hidden: "Media hidden",
  media_deleted: "Media deleted",
  media_restored: "Media restored",
  user_warned: "User warned",
  user_banned: "User banned",
  user_unbanned: "User unbanned",
  flag_reviewed: "Flag reviewed",
  flag_dismissed: "Flag dismissed",
  staff_added: "Staff added",
  staff_removed: "Staff removed",
  staff_role_changed: "Staff role changed",
  permission_granted: "Permission granted",
  permission_revoked: "Permission revoked"
};
var MODERATION_ACTION_CATEGORIES = {
  content: [
    "comment_hidden",
    "comment_deleted",
    "comment_restored",
    "media_hidden",
    "media_deleted",
    "media_restored"
  ],
  user: ["user_warned", "user_banned", "user_unbanned"],
  flag: ["flag_reviewed", "flag_dismissed"],
  staff: [
    "staff_added",
    "staff_removed",
    "staff_role_changed",
    "permission_granted",
    "permission_revoked"
  ]
};
var getModerationActionCategory = (action) => {
  for (const [category, actions] of Object.entries(MODERATION_ACTION_CATEGORIES)) {
    if (actions.includes(action)) {
      return category;
    }
  }
  return "content";
};

// src/validation/user.schema.ts
import { z } from "zod";
var userCreateSchema = z.object({
  id: z.string().min(1, "User ID is required"),
  googleId: z.string().min(1, "Google ID is required"),
  email: z.string().email("Invalid email format"),
  displayName: z.string().min(1, "Display name is required").max(100, "Display name must be 100 characters or less"),
  photoURL: z.string().url("Invalid photo URL").nullable(),
  isAdmin: z.boolean(),
  isOnboarded: z.boolean(),
  gradeDisplayPreference: z.enum(GRADE_DISPLAY_PREFERENCES).optional()
});
var userUpdateSchema = z.object({
  displayName: z.string().min(1, "Display name is required").max(100, "Display name must be 100 characters or less").optional(),
  photoURL: z.string().url("Invalid photo URL").nullable().optional(),
  gradeDisplayPreference: z.enum(GRADE_DISPLAY_PREFERENCES).optional()
});
var userSchema = userCreateSchema.extend({
  createdAt: z.union([z.date(), z.object({ seconds: z.number(), nanoseconds: z.number() })]),
  updatedAt: z.union([z.date(), z.object({ seconds: z.number(), nanoseconds: z.number() })])
});

// src/validation/gym.schema.ts
import { z as z3 } from "zod";

// src/validation/floor-plan.schema.ts
import { z as z2 } from "zod";
var pointSchema = z2.object({
  x: z2.number().min(0, "X must be at least 0").max(1, "X must be at most 1"),
  y: z2.number().min(0, "Y must be at least 0").max(1, "Y must be at most 1")
});
var wallSectionRegionSchema = z2.object({
  wallSectionName: z2.string().min(1, "Wall section name is required"),
  polygon: z2.array(pointSchema).min(3, "Polygon must have at least 3 points")
});
var gymFloorPlanSchema = z2.object({
  imageUrl: z2.string().url("Invalid image URL"),
  imageWidth: z2.number().int().positive("Image width must be a positive integer"),
  imageHeight: z2.number().int().positive("Image height must be a positive integer"),
  regions: z2.array(wallSectionRegionSchema)
}).refine(
  (data) => {
    const names = data.regions.map((r) => r.wallSectionName);
    return new Set(names).size === names.length;
  },
  {
    message: "Duplicate wall section names are not allowed in regions",
    path: ["regions"]
  }
);

// src/validation/gym.schema.ts
var colorGradeBaseSchema = z3.object({
  color: z3.string().min(1, "Color is required"),
  displayName: z3.string().min(1, "Display name is required"),
  suggestedVGrade: z3.enum(V_GRADES).nullable().optional()
});
var colorGradeSchema = z3.discriminatedUnion("associationType", [
  colorGradeBaseSchema.extend({ associationType: z3.literal("none") }),
  colorGradeBaseSchema.extend({
    associationType: z3.literal("exact"),
    exactGrade: z3.enum(V_GRADES)
  }),
  colorGradeBaseSchema.extend({
    associationType: z3.literal("range"),
    minGrade: z3.enum(V_GRADES),
    maxGrade: z3.enum(V_GRADES)
  })
]);
var labelingMethodsSchema = z3.object({
  holdColors: z3.boolean(),
  tagColors: z3.boolean(),
  primaryMethod: z3.enum(["holdColors", "tagColors"]),
  linkColors: z3.boolean()
});
var gymCreateSchema = z3.object({
  name: z3.string().min(1, "Gym name is required").max(100, "Gym name must be 100 characters or less"),
  description: z3.string().max(500, "Description must be 500 characters or less").nullable().optional(),
  address: z3.string().min(1, "Address is required"),
  city: z3.string().min(1, "City is required"),
  state: z3.string().min(1, "State is required"),
  postalCode: z3.string().max(20, "Postal code must be 20 characters or less").nullable().optional(),
  country: z3.string().min(1, "Country is required"),
  logoURL: z3.string().url("Invalid logo URL").nullable().optional(),
  colorGrades: z3.array(colorGradeSchema).min(1, "At least one color grade is required"),
  floorPlan: gymFloorPlanSchema.nullable().optional(),
  gradingSystem: z3.enum(GRADING_SYSTEMS).optional(),
  requireRouteName: z3.boolean().optional(),
  requireColorGrade: z3.boolean().optional(),
  ownerId: z3.string().min(1, "Owner ID is required")
});
var gymUpdateSchema = z3.object({
  name: z3.string().min(1, "Gym name is required").max(100, "Gym name must be 100 characters or less").optional(),
  description: z3.string().max(500, "Description must be 500 characters or less").nullable().optional(),
  address: z3.string().min(1, "Address is required").optional(),
  city: z3.string().min(1, "City is required").optional(),
  state: z3.string().min(1, "State is required").optional(),
  postalCode: z3.string().max(20, "Postal code must be 20 characters or less").nullable().optional(),
  country: z3.string().min(1, "Country is required").optional(),
  logoURL: z3.string().url("Invalid logo URL").nullable().optional(),
  colorGrades: z3.array(colorGradeSchema).min(1, "At least one color grade is required").optional(),
  floorPlan: gymFloorPlanSchema.nullable().optional(),
  gradingSystem: z3.enum(GRADING_SYSTEMS).optional(),
  requireRouteName: z3.boolean().optional(),
  requireColorGrade: z3.boolean().optional()
});
var gymSchema = gymCreateSchema.extend({
  id: z3.string().min(1),
  createdAt: z3.union([z3.date(), z3.object({ seconds: z3.number(), nanoseconds: z3.number() })]),
  updatedAt: z3.union([z3.date(), z3.object({ seconds: z3.number(), nanoseconds: z3.number() })])
});

// src/validation/route.schema.ts
import { z as z4 } from "zod";
var routePhotoSchema = z4.object({
  url: z4.string().url("Invalid photo URL"),
  thumbnailUrl: z4.string().url("Invalid thumbnail URL"),
  order: z4.number().int().min(0, "Order must be a non-negative integer")
});
var timestampSchema = z4.union([
  z4.date(),
  z4.object({ seconds: z4.number(), nanoseconds: z4.number() })
]);
var mapPositionSchema = z4.object({
  x: z4.number().min(0, "X must be >= 0").max(1, "X must be <= 1"),
  y: z4.number().min(0, "Y must be >= 0").max(1, "Y must be <= 1")
}).nullable();
var gradeModifierSchema = z4.enum(["+", "-"]).nullable().optional();
var routeIdentificationSchema = z4.object({
  holdColor: z4.string().optional(),
  tagColor: z4.string().optional()
}).optional();
var routeSetterSchema = z4.object({
  id: z4.string().min(1, "Setter ID is required"),
  name: z4.string().min(1, "Setter name is required")
});
var routePositionSchema = z4.enum(["left", "right", "center"]).nullable().optional();
var routeCreateSchema = z4.object({
  gymId: z4.string().min(1, "Gym ID is required"),
  name: z4.string().max(100, "Route name must be 100 characters or less").optional(),
  officialGrade: z4.enum(V_GRADES).nullable().optional(),
  officialGradeModifier: gradeModifierSchema,
  colorGrade: z4.string().optional(),
  identification: routeIdentificationSchema,
  wallSection: z4.string().optional(),
  position: routePositionSchema,
  description: z4.string().max(1e3, "Description must be 1000 characters or less").nullable().optional(),
  styles: z4.array(z4.enum(ROUTE_STYLES)).optional().default([]),
  photos: z4.array(routePhotoSchema).max(3, "Maximum 3 photos allowed").default([]),
  setters: z4.array(routeSetterSchema).min(1, "At least one setter is required"),
  assignedSetterId: z4.string().nullable(),
  status: z4.enum(ROUTE_STATUSES),
  setDate: timestampSchema,
  mapPosition: mapPositionSchema.optional()
});
var routeUpdateSchema = z4.object({
  name: z4.string().max(100, "Route name must be 100 characters or less").optional(),
  officialGrade: z4.enum(V_GRADES).nullable().optional(),
  officialGradeModifier: gradeModifierSchema,
  colorGrade: z4.string().optional(),
  identification: routeIdentificationSchema,
  wallSection: z4.string().optional(),
  position: routePositionSchema,
  description: z4.string().max(1e3, "Description must be 1000 characters or less").nullable().optional(),
  styles: z4.array(z4.enum(ROUTE_STYLES)).optional(),
  photos: z4.array(routePhotoSchema).max(3, "Maximum 3 photos allowed").optional(),
  setters: z4.array(routeSetterSchema).optional(),
  assignedSetterId: z4.string().nullable().optional(),
  status: z4.enum(ROUTE_STATUSES).optional(),
  retiredAt: timestampSchema.nullable().optional(),
  mapPosition: mapPositionSchema.optional()
});
var routeSchema = routeCreateSchema.extend({
  id: z4.string().min(1),
  retiredAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  // Deprecated fields for backward compatibility
  setterId: z4.string().optional(),
  setterName: z4.string().optional(),
  // Computed fields
  consensusGrade: z4.enum(V_GRADES).nullable(),
  consensusGradeModifier: gradeModifierSchema,
  gradeSubmissionCount: z4.number().int().min(0),
  averageRating: z4.number().min(1).max(5).nullable(),
  ratingCount: z4.number().int().min(0),
  totalSends: z4.number().int().min(0),
  totalAttempts: z4.number().int().min(0),
  mapPosition: mapPositionSchema
});

// src/validation/climb-log.schema.ts
import { z as z5 } from "zod";
var timestampSchema2 = z5.union([
  z5.date(),
  z5.object({ seconds: z5.number(), nanoseconds: z5.number() })
]);
var climbLogCreateSchema = z5.object({
  userId: z5.string().min(1, "User ID is required"),
  routeId: z5.string().min(1, "Route ID is required"),
  gymId: z5.string().min(1, "Gym ID is required"),
  outcome: z5.enum(CLIMB_OUTCOMES),
  notes: z5.string().max(500, "Notes must be 500 characters or less").nullable().optional(),
  loggedAt: timestampSchema2
});
var climbLogSchema = climbLogCreateSchema.extend({
  id: z5.string().min(1),
  createdAt: timestampSchema2
});

// src/validation/grade-submission.schema.ts
import { z as z6 } from "zod";
var timestampSchema3 = z6.union([
  z6.date(),
  z6.object({ seconds: z6.number(), nanoseconds: z6.number() })
]);
var gradeSubmissionCreateSchema = z6.object({
  userId: z6.string().min(1, "User ID is required"),
  routeId: z6.string().min(1, "Route ID is required"),
  grade: z6.enum(V_GRADES)
});
var gradeSubmissionSchema = gradeSubmissionCreateSchema.extend({
  id: z6.string().min(1),
  odKey: z6.string().min(1),
  createdAt: timestampSchema3,
  updatedAt: timestampSchema3
});

// src/validation/quality-rating.schema.ts
import { z as z7 } from "zod";
var timestampSchema4 = z7.union([
  z7.date(),
  z7.object({ seconds: z7.number(), nanoseconds: z7.number() })
]);
var qualityRatingCreateSchema = z7.object({
  userId: z7.string().min(1, "User ID is required"),
  routeId: z7.string().min(1, "Route ID is required"),
  rating: z7.union([z7.literal(1), z7.literal(2), z7.literal(3), z7.literal(4), z7.literal(5)], {
    errorMap: () => ({ message: "Rating must be between 1 and 5" })
  })
});
var qualityRatingSchema = qualityRatingCreateSchema.extend({
  id: z7.string().min(1),
  odKey: z7.string().min(1),
  createdAt: timestampSchema4,
  updatedAt: timestampSchema4
});
var validateRatingValue = (value) => {
  return typeof value === "number" && Number.isInteger(value) && QUALITY_RATING_VALUES.includes(value);
};

// src/validation/invitation.schema.ts
import { z as z8 } from "zod";
var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var inviteSetterRequestSchema = z8.object({
  /** ID of the gym to invite the setter to */
  gymId: z8.string().min(1, "Gym ID is required").max(100, "Gym ID is too long"),
  /** Email address of the person to invite */
  email: z8.string().min(1, "Email is required").max(254, "Email is too long").regex(emailPattern, "Invalid email format").transform((email) => email.toLowerCase().trim())
});
var acceptInvitationRequestSchema = z8.object({
  /** The invitation token from the email link */
  token: z8.string().min(1, "Token is required").max(100, "Token is too long")
});
var resendInvitationRequestSchema = z8.object({
  /** ID of the invitation to resend */
  invitationId: z8.string().min(1, "Invitation ID is required").max(100, "Invitation ID is too long")
});
var cancelInvitationRequestSchema = z8.object({
  /** ID of the invitation to cancel */
  invitationId: z8.string().min(1, "Invitation ID is required").max(100, "Invitation ID is too long")
});
var validateEmail = (email) => {
  return emailPattern.test(email);
};

// src/validation/gym-request.schema.ts
import { z as z9 } from "zod";
var createGymRequestSchema = z9.object({
  /** Name of the gym being requested */
  name: z9.string().min(2, "Name must be at least 2 characters").max(100, "Name must be at most 100 characters"),
  /** Description of the gym */
  description: z9.string().min(10, "Description must be at least 10 characters").max(1e3, "Description must be at most 1000 characters"),
  /** Street address of the gym */
  address: z9.string().min(1, "Address is required"),
  /** City where the gym is located */
  city: z9.string().min(1, "City is required"),
  /** State/province where the gym is located */
  state: z9.string().min(1, "State is required"),
  /** Country where the gym is located */
  country: z9.string().min(1, "Country is required")
});
var updateGymRequestSchema = createGymRequestSchema.partial();
var reviewGymRequestSchema = z9.object({
  /** ID of the request being reviewed */
  requestId: z9.string().min(1, "Request ID is required"),
  /** Action to take on the request */
  action: z9.enum(["approve", "reject", "request_changes"]),
  /** Notes explaining the decision (required for reject/request_changes) */
  notes: z9.string().optional()
}).refine((data) => data.action === "approve" || data.notes && data.notes.length >= 10, {
  message: "Notes are required for reject or request changes (min 10 chars)",
  path: ["notes"]
});

// src/validation/owner-invitation.schema.ts
import { z as z10 } from "zod";
var emailPattern2 = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var inviteOwnerSchema = z10.object({
  /** ID of the gym to invite the owner to */
  gymId: z10.string().min(1, "Gym ID is required").max(100, "Gym ID is too long"),
  /** Email address of the person to invite */
  email: z10.string().min(1, "Email is required").max(254, "Email is too long").regex(emailPattern2, "Invalid email format").transform((email) => email.toLowerCase().trim())
});
var acceptOwnerInvitationSchema = z10.object({
  /** The invitation token from the email link */
  token: z10.string().min(1, "Token is required").max(100, "Token is too long")
});

// src/validation/flyer.schema.ts
import { z as z11 } from "zod";
var MAX_LOGO_BASE64_LENGTH = 7 * 1024 * 1024;
var generateFlyerRequestSchema = z11.object({
  /** Name of the gym to display on the flyer (optional if logo provided) */
  gymName: z11.string().max(100, "Gym name is too long").optional(),
  /** Size of the flyer: 'letter' (8.5x11") or 'half-letter' (5.5x8.5") */
  size: z11.enum(["letter", "half-letter"]).default("letter"),
  /** Output format */
  format: z11.enum(["png", "svg"]).default("png"),
  /** Optional gym logo as base64-encoded PNG (without data URI prefix) */
  gymLogoBase64: z11.string().max(MAX_LOGO_BASE64_LENGTH, "Logo file is too large (max 5MB)").refine(
    (val) => {
      try {
        return /^[A-Za-z0-9+/]*={0,2}$/.test(val) && val.length % 4 === 0;
      } catch {
        return false;
      }
    },
    { message: "Invalid base64 format for logo" }
  ).optional()
}).refine((data) => data.gymName?.trim() || data.gymLogoBase64, {
  message: "Either gym name or gym logo must be provided"
});

// src/logic/consensus-grade.ts
var MINIMUM_SUBMISSIONS_FOR_CONSENSUS = 3;
var SETTER_GRADE_WEIGHT = 3;
var getGradeIndex2 = (grade) => {
  return V_GRADES.indexOf(grade);
};
var calculateMedian = (sortedGrades) => {
  const length = sortedGrades.length;
  const midIndex = Math.floor(length / 2);
  if (length % 2 === 1) {
    return sortedGrades[midIndex];
  } else {
    return sortedGrades[midIndex - 1];
  }
};
var calculateConsensusGrade = (grades, setterGrade) => {
  const weightedGrades = [...grades];
  if (setterGrade) {
    for (let i = 0; i < SETTER_GRADE_WEIGHT; i++) {
      weightedGrades.push(setterGrade);
    }
  }
  if (weightedGrades.length < MINIMUM_SUBMISSIONS_FOR_CONSENSUS) {
    return null;
  }
  const sortedGrades = [...weightedGrades].sort((a, b) => getGradeIndex2(a) - getGradeIndex2(b));
  return calculateMedian(sortedGrades);
};
var calculateConsensusGradeWithMetadata = (grades, setterGrade) => {
  const distribution = /* @__PURE__ */ new Map();
  for (const grade of grades) {
    const currentCount = distribution.get(grade) ?? 0;
    distribution.set(grade, currentCount + 1);
  }
  return {
    consensusGrade: calculateConsensusGrade(grades, setterGrade),
    submissionCount: grades.length,
    distribution,
    setterGradeIncluded: setterGrade != null
  };
};

// src/logic/average-rating.ts
var calculateAverageRating = (ratings) => {
  if (ratings.length === 0) {
    return null;
  }
  const sum = ratings.reduce((acc, rating) => acc + rating, 0);
  const average = sum / ratings.length;
  return Math.round(average * 10) / 10;
};
var calculateAverageRatingWithMetadata = (ratings) => {
  const distribution = /* @__PURE__ */ new Map();
  for (const rating of ratings) {
    const currentCount = distribution.get(rating) ?? 0;
    distribution.set(rating, currentCount + 1);
  }
  return {
    averageRating: calculateAverageRating(ratings),
    ratingCount: ratings.length,
    distribution
  };
};

// src/logic/permissions.ts
var canEditRoute = (userId, route, membership) => {
  if (!membership) {
    return false;
  }
  if (membership.userId !== userId) {
    return false;
  }
  if (membership.gymId !== route.gymId) {
    return false;
  }
  if (membership.role === "owner") {
    return true;
  }
  if (membership.role === "setter" && route.setterId === userId) {
    return true;
  }
  return false;
};
var canRetireRoute = (userId, route, membership) => {
  return canEditRoute(userId, route, membership);
};
var canManageGym = (userId, membership) => {
  if (!membership) {
    return false;
  }
  if (membership.userId !== userId) {
    return false;
  }
  return membership.role === "owner";
};
var canCreateRoute = (userId, membership) => {
  if (!membership) {
    return false;
  }
  if (membership.userId !== userId) {
    return false;
  }
  return membership.role === "owner" || membership.role === "setter";
};
var canInviteSetters = (userId, membership) => {
  return canManageGym(userId, membership);
};
var canRemoveSetter = (userId, membership) => {
  return canManageGym(userId, membership);
};
var hasMinimumRole = (userRole, requiredRole) => {
  const roleHierarchy = ["owner", "setter", "climber"];
  const userIndex = roleHierarchy.indexOf(userRole);
  const requiredIndex = roleHierarchy.indexOf(requiredRole);
  return userIndex !== -1 && requiredIndex !== -1 && userIndex <= requiredIndex;
};

// src/logic/grade-utils.ts
var parseGradeNumber = (grade) => {
  if (grade === "VB") return -1;
  return parseInt(grade.substring(1), 10);
};
var gradeToString = (gradeNumber) => {
  if (gradeNumber === -1) return "VB";
  if (!Number.isInteger(gradeNumber) || gradeNumber < 0 || gradeNumber > 17) {
    return null;
  }
  return `V${gradeNumber}`;
};
var isHarderThan = (a, b) => {
  return parseGradeNumber(a) > parseGradeNumber(b);
};
var isEasierThan = (a, b) => {
  return parseGradeNumber(a) < parseGradeNumber(b);
};
var getGradeRange = (min, max) => {
  let minIndex = V_GRADES.indexOf(min);
  let maxIndex = V_GRADES.indexOf(max);
  if (minIndex > maxIndex) {
    [minIndex, maxIndex] = [maxIndex, minIndex];
  }
  return V_GRADES.slice(minIndex, maxIndex + 1);
};
var getGradeDifference = (a, b) => {
  return parseGradeNumber(a) - parseGradeNumber(b);
};
var normalizeGrade = (input) => {
  const trimmed = input.trim();
  const normalized = trimmed.toUpperCase();
  if (isVGrade(normalized)) {
    return normalized;
  }
  return null;
};
var getNextHarderGrade = (grade) => {
  const index = V_GRADES.indexOf(grade);
  if (index === -1 || index >= V_GRADES.length - 1) {
    return null;
  }
  const nextGrade = V_GRADES[index + 1];
  return nextGrade !== void 0 ? nextGrade : null;
};
var getNextEasierGrade = (grade) => {
  const index = V_GRADES.indexOf(grade);
  if (index <= 0) {
    return null;
  }
  const prevGrade = V_GRADES[index - 1];
  return prevGrade !== void 0 ? prevGrade : null;
};

// src/moderation/content-filter.ts
var CONTENT_CATEGORIES = [
  "profanity",
  "harassment",
  "threat",
  "explicit",
  "hate_speech",
  "spam"
];
var PROFANITY_PATTERNS = [
  // Common profanity (with letter substitution patterns)
  /\bf+[u*@]+c+k+(?:ing|er|ed|s)?\b/gi,
  /\bs+h+[i1!]+t+(?:ty|s|ting)?\b/gi,
  /\ba+s+s+(?:hole|wipe|hat)?\b/gi,
  /\bb+[i1!]+t+c+h+(?:es|ing|y)?\b/gi,
  /\bd+[a@]+m+n+(?:ed|it)?\b/gi,
  /\bc+u+n+t+s?\b/gi,
  /\bd+[i1!]+c+k+(?:s|head)?\b/gi,
  /\bp+[i1!]+s+s+(?:ed|ing|y)?\b/gi,
  /\bwh+[o0]+r+e+s?\b/gi,
  /\bb+a+s+t+a+r+d+s?\b/gi
];
var HARASSMENT_PATTERNS = [
  /\b(?:kill|hurt|harm)\s+(?:your)?self\b/gi,
  /\bk+y+s+\b/gi,
  // "kill yourself" abbreviation
  /\bgo\s+die\b/gi,
  /\byou(?:'re|\s+are)?\s+(?:worthless|pathetic|useless|garbage|trash)\b/gi,
  /\bnobody\s+(?:likes|cares\s+about|wants)\s+you\b/gi,
  /\bfa+g+(?:got)?\b/gi,
  /\bre+t+a+r+d+(?:ed)?\b/gi,
  /\bget\s+(?:cancer|aids|covid)\b/gi,
  /\bhope\s+you\s+die\b/gi
];
var THREAT_PATTERNS = [
  /\bi(?:'ll|m\s+going\s+to|will)\s+(?:kill|murder|hurt|beat|stab|shoot)\s+you\b/gi,
  /\bi\s+know\s+where\s+you\s+live\b/gi,
  /\byou(?:'re|\s+are)?\s+(?:dead|gonna\s+die)\b/gi,
  /\bi(?:'ll|m\s+going\s+to)\s+find\s+you\b/gi,
  /\bwatch\s+your\s+back\b/gi
];
var EXPLICIT_PATTERNS = [
  /\bs+e+x+(?:ual|y)?\b/gi,
  /\bp+[o0]+r+n+(?:o|ography)?\b/gi,
  /\bn+[u*]+d+e+s?\b/gi,
  /\bt+[i1!]+t+(?:s|ties)?\b/gi,
  /\bb+[o0]+[o0]+b+(?:s|ies)?\b/gi,
  /\bc+[o0]+c+k+s?\b/gi,
  /\bp+u+s+s+y+\b/gi
];
var HATE_SPEECH_PATTERNS = [
  /\bn+[i1!]+g+(?:g+[e3]+r+|a+)s?\b/gi,
  /\bk+[i1!]+k+e+s?\b/gi,
  /\bs+p+[i1!]+c+(?:k)?s?\b/gi,
  /\bc+h+[i1!]+n+k+s?\b/gi,
  /\bg+[o0]+[o0]+k+s?\b/gi
];
var SPAM_PATTERNS = [
  // Suspicious URLs
  /(?:https?:\/\/)?(?:www\.)?(?:bit\.ly|tinyurl|t\.co|goo\.gl)\/\S+/gi,
  // Excessive repeated characters (5+ of same char)
  /(.)\1{4,}/g,
  // Money/crypto spam
  /\b(?:free\s+)?(?:bitcoin|crypto|money|cash|prize)\s+(?:giveaway|free|win)/gi,
  /\b(?:dm|message)\s+(?:me|for)\s+(?:deals?|offers?|more)/gi
];
var PATTERN_CATEGORIES = [
  { category: "profanity", patterns: PROFANITY_PATTERNS },
  { category: "harassment", patterns: HARASSMENT_PATTERNS },
  { category: "threat", patterns: THREAT_PATTERNS },
  { category: "explicit", patterns: EXPLICIT_PATTERNS },
  { category: "hate_speech", patterns: HATE_SPEECH_PATTERNS },
  { category: "spam", patterns: SPAM_PATTERNS }
];
var CATEGORY_SEVERITY = {
  spam: 0.3,
  profanity: 0.5,
  explicit: 0.6,
  harassment: 0.8,
  hate_speech: 0.9,
  threat: 1
};
var filterContent = (text, options = {}) => {
  const {
    categories: checkCategories = CONTENT_CATEGORIES,
    minConfidence = 0.3,
    generateCleanedText = false
  } = options;
  const detectedCategories = [];
  const matches = [];
  let maxSeverity = 0;
  let cleanedText = text;
  for (const { category, patterns } of PATTERN_CATEGORIES) {
    if (!checkCategories.includes(category)) continue;
    for (const pattern of patterns) {
      pattern.lastIndex = 0;
      let match;
      while ((match = pattern.exec(text)) !== null) {
        if (!detectedCategories.includes(category)) {
          detectedCategories.push(category);
        }
        matches.push(match[0]);
        maxSeverity = Math.max(maxSeverity, CATEGORY_SEVERITY[category]);
        if (generateCleanedText) {
          const replacement = "*".repeat(match[0].length);
          cleanedText = cleanedText.replace(match[0], replacement);
        }
      }
    }
  }
  const matchBonus = Math.min(matches.length * 0.1, 0.3);
  const confidence = Math.min(maxSeverity + matchBonus, 1);
  const flagged = detectedCategories.length > 0 && confidence >= minConfidence;
  return {
    flagged,
    categories: detectedCategories,
    matches,
    confidence,
    ...generateCleanedText && { cleanedText }
  };
};
var containsProfanity = (text) => {
  for (const pattern of PROFANITY_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) return true;
  }
  return false;
};
var containsThreats = (text) => {
  for (const pattern of THREAT_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) return true;
  }
  return false;
};
var containsHarassment = (text) => {
  for (const pattern of HARASSMENT_PATTERNS) {
    pattern.lastIndex = 0;
    if (pattern.test(text)) return true;
  }
  return false;
};
var isContentSafe = (text) => {
  const result = filterContent(text);
  return !result.flagged;
};
var getBlockedContentMessage = (categories) => {
  if (categories.includes("threat")) {
    return "This message contains threatening language and cannot be posted.";
  }
  if (categories.includes("hate_speech")) {
    return "This message contains hate speech and cannot be posted.";
  }
  if (categories.includes("harassment")) {
    return "This message contains harassment and cannot be posted.";
  }
  if (categories.includes("explicit")) {
    return "This message contains explicit content and cannot be posted.";
  }
  if (categories.includes("profanity")) {
    return "This message contains inappropriate language. Please revise and try again.";
  }
  if (categories.includes("spam")) {
    return "This message appears to be spam and cannot be posted.";
  }
  return "This message cannot be posted due to content policy violations.";
};

// src/index.ts
var VERSION = "0.1.0";
export {
  APP_ROLES,
  BAN_DURATIONS,
  CLIMBING_STYLES,
  CLIMBING_STYLE_INFO,
  CLIMB_OUTCOMES,
  COMMENT_MODERATION_STATUSES,
  CONTENT_CATEGORIES,
  FEEDBACK_PRIORITIES,
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  FLAG_ACTIONS,
  FLAG_CONTENT_TYPES,
  FLAG_REASONS,
  FLAG_REASON_LABELS,
  FLAG_STATUSES,
  FLYER_FORMATS,
  FLYER_SIZES,
  GRADE_DISPLAY_PREFERENCES,
  GRADE_MODIFIERS,
  GRADING_SYSTEMS,
  GYM_PERMISSIONS,
  GYM_REQUEST_STATUSES,
  GYM_ROLES,
  HOLD_STYLES,
  HOLD_STYLE_INFO,
  INVITATION_EXPIRY_DAYS,
  INVITATION_STATUSES,
  MAX_COMMENT_LENGTH,
  MAX_FLAG_DETAILS_LENGTH,
  MAX_GYM_MEMBERSHIPS,
  MAX_LOGO_BASE64_LENGTH,
  MAX_REPLY_DEPTH,
  MINIMUM_SUBMISSIONS_FOR_CONSENSUS,
  MODERATION_ACTIONS,
  MODERATION_ACTION_CATEGORIES,
  MODERATION_ACTION_LABELS,
  MODERATION_TARGET_TYPES,
  OWNER_INVITATION_EXPIRY_DAYS,
  QUALITY_RATING_VALUES,
  ROLE_DEFAULT_PERMISSIONS,
  ROUTE_STATUSES,
  ROUTE_STYLES,
  ROUTE_STYLE_CATEGORIES,
  ROUTE_STYLE_INFO,
  SETTER_GRADE_WEIGHT,
  SKILL_STYLES,
  SKILL_STYLE_INFO,
  STAFF_ROLES,
  VERSION,
  V_GRADES,
  acceptInvitationRequestSchema,
  acceptOwnerInvitationSchema,
  calculateAverageRating,
  calculateAverageRatingWithMetadata,
  calculateConsensusGrade,
  calculateConsensusGradeWithMetadata,
  canCreateRoute,
  canEditRoute,
  canInviteSetters,
  canManageGym,
  canRemoveSetter,
  canRetireRoute,
  cancelInvitationRequestSchema,
  climbLogCreateSchema,
  climbLogSchema,
  colorGradeSchema,
  compareGrades,
  containsHarassment,
  containsProfanity,
  containsThreats,
  createGymRequestSchema,
  filterContent,
  generateFlyerRequestSchema,
  getBlockedContentMessage,
  getDefaultPermissions,
  getEffectivePermissions,
  getGradeDifference,
  getGradeIndex,
  getGradeRange,
  getGradeSubmissionId,
  getGymMembershipId,
  getModerationActionCategory,
  getNextEasierGrade,
  getNextHarderGrade,
  getQualityRatingId,
  getWallSections,
  gradeSubmissionCreateSchema,
  gradeSubmissionSchema,
  gradeToString,
  gymCreateSchema,
  gymFloorPlanSchema,
  gymSchema,
  gymUpdateSchema,
  hasMinimumRole,
  hasPermission,
  hasRolePrivilege,
  inviteOwnerSchema,
  inviteSetterRequestSchema,
  isAppRole,
  isClimbOutcome,
  isCommentModerationStatus,
  isContentSafe,
  isEasierThan,
  isFeedbackPriority,
  isFeedbackStatus,
  isFeedbackType,
  isFlagContentType,
  isFlagReason,
  isFlagStatus,
  isFlyerFormat,
  isFlyerSize,
  isGradeDisplayPreference,
  isGradeModifier,
  isGradingSystem,
  isGymPermission,
  isGymRequestStatus,
  isGymRole,
  isHarderThan,
  isInvitationExpired,
  isInvitationStatus,
  isModerationAction,
  isOwnerInvitationExpired,
  isQualityRatingValue,
  isReply,
  isRouteStatus,
  isRouteStyle,
  isStaffRole,
  isVGrade,
  isValidCommentText,
  isValidFlagDetails,
  normalizeGrade,
  normalizeGymRole,
  normalizeRouteStatus,
  normalizeRouteStyle,
  normalizeRouteStyles,
  parseGradeNumber,
  pointSchema,
  qualityRatingCreateSchema,
  qualityRatingSchema,
  resendInvitationRequestSchema,
  reviewGymRequestSchema,
  roleHasDefaultPermission,
  routeCreateSchema,
  routePhotoSchema,
  routeSchema,
  routeUpdateSchema,
  updateGymRequestSchema,
  userCreateSchema,
  userSchema,
  userUpdateSchema,
  validateEmail,
  validateRatingValue,
  wallSectionRegionSchema
};
//# sourceMappingURL=index.js.map